import { useState, useEffect } from 'react';
import { Building, Save } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';
import channelService, { type Channel } from '../../services/channel.service';

interface TenantFormData {
  id: string;
  name: string;             // maps to ten_name
  domain?: string;          // maps to ten_domain (auto-generated if empty)
  // Free-text "Organization Name" (ten_org_name), create only. Display only —
  // NOT the channel, and never an input to scoping or authorization.
  organizationName?: string;
  description?: string;     // maps to ten_desc
  defaultRole?: string;     // Default role for new users (Add mode only)
  maxVpnDevicesPerUser?: number; // SuperAdmin only: per-tenant security setting
  // Owning channel, create only. Reassigning a tenant between channels is not
  // offered here — it is SuperAdmin-only and has no endpoint on the tenant API.
  channelId?: string;
}

interface TenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tenant: TenantFormData) => void;
  tenant?: any; // Can accept API format
  mode: 'add' | 'edit';
}

const TenantModal = ({ isOpen, onClose, onSave, tenant, mode }: TenantModalProps) => {
  const toast = useToast();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const isChannelAdmin = user?.type === USER_TYPES.CHANNEL_ADMIN;
  const [channels, setChannels] = useState<Channel[]>([]);
  const [formData, setFormData] = useState<TenantFormData>({
    id: '',
    name: '',
    organizationName: '',
    description: '',
    defaultRole: 'Editor',
    maxVpnDevicesPerUser: 5,
  });

  // Channel picker options, when creating a tenant.
  //
  // SuperAdmin: every channel, plus a "Direct (no channel)" option.
  // ChannelAdmin: only the channels they administer (channelService is
  //   channel-scoped), never "Direct" — a ChannelAdmin cannot create a tenant
  //   outside a channel. With exactly one channel the server forces it, so we
  //   preselect and could even omit the field; with several the server returns
  //   400 unless one is named, so the picker requires a choice.
  useEffect(() => {
    if (!isOpen || mode !== 'add' || !(isSuperAdmin || isChannelAdmin)) return;
    channelService
      .listAll()
      .then((items) => {
        setChannels(items);
        // Preselect the sole channel for a single-channel ChannelAdmin.
        if (isChannelAdmin && items.length === 1) {
          setFormData((prev) => ({ ...prev, channelId: items[0].id }));
        }
      })
      .catch(() => setChannels([]));
  }, [isOpen, mode, isSuperAdmin, isChannelAdmin]);

  // Update form when tenant prop changes
  useEffect(() => {
    if (tenant) {
      // Map API format to form format
      setFormData({
        id: tenant.id || '',
        name: tenant.name || tenant.ten_name || '',
        organizationName: tenant.organizationName || tenant.ten_org_name || '',
        description: tenant.description || tenant.ten_desc || '',
        maxVpnDevicesPerUser: tenant.maxVpnDevicesPerUser ?? 5,
      });
    } else {
      // Reset for add mode
      setFormData({
        id: '',
        name: '',
        organizationName: '',
        description: '',
        defaultRole: 'Editor',
        maxVpnDevicesPerUser: 5,
      });
    }
  }, [tenant, isOpen]);

  const handleChange = (field: keyof TenantFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // A ChannelAdmin's tenant always belongs to a channel — never "Direct". With
  // one channel it is preselected; with several the user must choose. Requiring
  // channelId for any ChannelAdmin (not just the multi-channel case) also closes
  // the window where the channels fetch hasn't resolved yet: submitting then
  // would otherwise send no channelId and draw a confusing server 400. A
  // SuperAdmin may legitimately leave it "Direct", so this applies to them not.
  //
  // ONLY in add mode: the channel picker is create-only (reassigning a tenant's
  // channel isn't offered here), channels are only fetched for add mode, and
  // channelId is never populated on the edit form — so requiring it on edit was
  // an unsatisfiable dead end that blocked every ChannelAdmin tenant edit with
  // "Channels are still loading".
  const mustPickChannel = isChannelAdmin && mode === 'add';

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.name || formData.name.trim().length === 0) {
      toast.error('Validation Error', 'Tenant name is required');
      return;
    }

    if (mustPickChannel && !formData.channelId) {
      toast.error(
        'Validation Error',
        channels.length === 0
          ? 'Channels are still loading — please try again in a moment.'
          : 'Select the channel this tenant belongs to',
      );
      return;
    }

    onSave(formData);
    onClose();
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button onClick={handleSubmit} className="btn-primary inline-flex items-center space-x-2">
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create Tenant' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add New Tenant' : `Edit Tenant: ${tenant?.name || tenant?.ten_name}`}
      size="2xl"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <Building className="w-5 h-5 text-primary-600" />
            <span>Basic Information</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tenant Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Acme Corp"
                className="input-base w-full"
                required
              />
            </div>

            {mode === 'add' && (isSuperAdmin || isChannelAdmin) && channels.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel {mustPickChannel && <span className="text-red-500">*</span>}
                </label>
                <select
                  value={formData.channelId || ''}
                  onChange={(e) => handleChange('channelId', e.target.value)}
                  className="input-base w-full"
                  // A single-channel ChannelAdmin has exactly one valid target,
                  // already preselected — no point letting them change it.
                  disabled={isChannelAdmin && channels.length === 1}
                >
                  {/* Only a SuperAdmin may create a channel-less "direct" tenant. */}
                  {isSuperAdmin && <option value="">Direct (no channel)</option>}
                  {isChannelAdmin && channels.length > 1 && <option value="">Select a channel…</option>}
                  {channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  The channel partner that will own this tenant. Cannot be changed here later.
                </p>
              </div>
            )}

            {mode === 'add' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default User Role
                </label>
                <select
                  value={formData.defaultRole}
                  onChange={(e) => handleChange('defaultRole', e.target.value)}
                  className="input-base w-full"
                >
                  <option value="Admin">Admin (Full Access)</option>
                  <option value="Editor">Editor (Read/Write)</option>
                  <option value="Viewer">Viewer (Read Only)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  This role will be set as default for new users invited to this tenant.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Details</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional description or notes about this tenant..."
                className="input-base w-full"
                rows={3}
              />
            </div>

            {isSuperAdmin && mode === 'edit' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max VPN Devices Per User
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={formData.maxVpnDevicesPerUser ?? 5}
                  onChange={(e) => handleChange('maxVpnDevicesPerUser', parseInt(e.target.value, 10) || 1)}
                  className="input-base w-32"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Default applied to new VPN users.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TenantModal;
